/**
 * Runtime configuration.
 *
 * Backend CLAUDE.md §5b: "**Environment is explicit in config, never inferred**
 * from hostname, branch or a default. Same posture as `WALLET_CELL_ID` /
 * `WALLET_CELL_ROLE`: required, no default, refuse to start without it."
 *
 * and: "**Promotion is by image digest, not by rebuild.** The image that passed
 * staging is the image that goes to production."
 *
 * Those two together are why this file exists. A Vite `import.meta.env` constant
 * would satisfy the first and break the second: baking the API base URL into the
 * bundle makes staging and production different artifacts, so the digest that
 * passed staging could not be the digest that ships. Configuration is therefore
 * fetched from `/config.json` at boot, which the container entrypoint renders
 * from environment variables — see `docker-entrypoint.sh`.
 *
 * There is no fallback and no default in this module. If `/config.json` is
 * missing, malformed, or omits a required key, the app renders a failure and
 * stops. A frontend that guesses its backend is a frontend that will one day
 * point staging at production.
 */

export const ENVIRONMENTS = ['local', 'staging', 'production'] as const
export type Environment = (typeof ENVIRONMENTS)[number]

export interface AppConfig {
  /** Which deployment this is. Explicit, never inferred from location.hostname. */
  readonly environment: Environment
  /** Base URL of the go-wallet REST surface, e.g. https://bck.staging.tesora.money */
  readonly apiBaseUrl: string
  /** Ory SDK URL. Non-secret by design (backend CLAUDE.md §6). */
  readonly orySdkUrl: string
  /** Ory project id. An identifier, not a credential (backend CLAUDE.md §6). */
  readonly oryProjectId: string
  /** Image tag this bundle shipped as, for the footer and for bug reports. */
  readonly release: string
}

export class ConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ConfigError'
  }
}

function requireString(o: Record<string, unknown>, key: string): string {
  const v = o[key]
  if (typeof v !== 'string' || v.trim() === '') {
    throw new ConfigError(
      `/config.json is missing required key ${JSON.stringify(key)}. ` +
        'Configuration is required with no default (backend CLAUDE.md §5b); ' +
        'set the corresponding TESORA_* environment variable on the container.',
    )
  }
  return v
}

export function parseConfig(raw: unknown): AppConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new ConfigError('/config.json did not contain a JSON object')
  }
  const o = raw as Record<string, unknown>

  const environment = requireString(o, 'environment')
  if (!(ENVIRONMENTS as readonly string[]).includes(environment)) {
    throw new ConfigError(
      `environment ${JSON.stringify(environment)} is not one of ${ENVIRONMENTS.join(', ')}`,
    )
  }

  const apiBaseUrl = requireString(o, 'apiBaseUrl').replace(/\/+$/, '')
  let parsedApi: URL
  try {
    parsedApi = new URL(apiBaseUrl)
  } catch {
    throw new ConfigError(`apiBaseUrl ${JSON.stringify(apiBaseUrl)} is not an absolute URL`)
  }
  // A staging bundle that has been handed a production API base is the failure
  // this whole module exists to prevent, and it is worth being loud about.
  if (environment === 'production' && parsedApi.protocol !== 'https:') {
    throw new ConfigError('production apiBaseUrl must be https')
  }

  return {
    environment: environment as Environment,
    apiBaseUrl,
    orySdkUrl: requireString(o, 'orySdkUrl'),
    oryProjectId: requireString(o, 'oryProjectId'),
    release: requireString(o, 'release'),
  }
}

let cached: AppConfig | null = null

/**
 * Fetch and validate `/config.json`.
 *
 * `cache: 'no-store'` because the config file is the one asset that must never
 * be served from a stale cache: a redeploy that changes the API base and a
 * browser that keeps the old one is a silent cross-environment call.
 */
export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  if (cached !== null) return cached
  let res: Response
  try {
    res = await fetchImpl('/config.json', { cache: 'no-store' })
  } catch (cause) {
    throw new ConfigError(`could not fetch /config.json: ${String(cause)}`)
  }
  if (!res.ok) {
    throw new ConfigError(`/config.json returned HTTP ${res.status}`)
  }
  let body: unknown
  try {
    body = await res.json()
  } catch {
    throw new ConfigError('/config.json is not valid JSON')
  }
  cached = parseConfig(body)
  return cached
}

/** Test seam. Not exported to the app. */
export function __resetConfigForTests(): void {
  cached = null
}
